import { PaginationTable, Table, TableWrapper } from 'components/Global';
import React, { useEffect } from 'react';
import { ReactComponent as DriverIcon } from 'icons/driving-wheel-icon.svg';
import { ReactComponent as Star } from 'icons/star.svg';
import EmptyState from 'components/Global/EmptyState';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from 'components/Context/AppContext';
import { deleteDriverById, getAllDrivers, getDetailDriver } from 'features/drivers/actions';
import LoadingSpinner from 'components/Global/LoadingSpinner';
import { resetSelectedDriver, setPage } from 'features/drivers/slice';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import ActionButtons from 'components/Global/Table/ActionButtons';
import StatusButton from 'components/Global/Table/StatusButton';
import { trackEvent } from 'utils/analytics';

const column = [
  { header: 'No.', value: 'no' },
  { header: 'Nama', value: 'name' },
  { header: 'Email', value: 'email' },
  { header: 'No. Handphone', value: 'phone_number' },
  { header: 'Status', value: 'status' },
];

const newColumn = [
  { header: 'No.', value: 'no' },
  { header: 'Nama', value: 'name' },
  { header: 'Email', value: 'email' },
  { header: 'No. Handphone', value: 'phone_number' },
  { header: 'Rating Driver', value: 'rating' },
  { header: 'Status', value: 'status' },
];

const CustomDriverTable = ({ data, currentPage, handleDetail, handleEdit, handleDelete, column }) => {
  const pageNumber = (page, idx) => (page > 1 ? (page - 1) * 10 + idx : idx);

  const rows = 10 - data?.length;

  return (
    <div className="table">
      <table className="table-wrapper">
        <thead className="table-head">
          <tr>
            {column.map((item) => (
              <th key={item.value}>{item.header}</th>
            ))}
            <th className="action-col">Action</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {data?.length > 0 &&
            data.map((item, no) => (
              <tr key={item.id}>
                {column.map((col) => {
                  if (col.value === 'no') {
                    return (
                      <td
                        key={col.value}
                        data-type="no"
                        className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                      >
                        {pageNumber(currentPage, no + 1)}
                      </td>
                    );
                  } else if (col.value === 'status') {
                    return (
                      <td
                        key={col.value}
                        className={`pre-white-space address_details ${
                          col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''
                        }`}
                      >
                        {<StatusButton status={item[col.value].toLowerCase()} />}
                      </td>
                    );
                  } else if (col.value === 'rating') {
                    const ratingValue = item[col.value];
                    const hasRating = ratingValue != null;

                    return (
                      <td
                        key={col.value}
                        className={`pre-white-space address_details ${
                          col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''
                        }`}
                      >
                        <div>
                          {hasRating ? (
                            <p className="rating-driver">
                              <Star color="#f1a33a" />
                              <span className="rating-value">{ratingValue}</span>
                              <span>/5</span>
                            </p>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                    );
                  } else {
                    return (
                      <td
                        key={col.value}
                        data-type={col.value}
                        className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                      >
                        {item[col.value]}
                      </td>
                    );
                  }
                })}
                <td className="btn-action" data-type="actionButtons" data-key={!item.id ? item.code : item.id}>
                  <ActionButtons
                    detail={handleDetail}
                    edit={item.status === 'waiting_approval' ? false : handleEdit}
                    del={item.status === 'waiting_approval' ? false : handleDelete}
                  />
                </td>
              </tr>
            ))}
          {rows > 0 &&
            Array.from({ length: rows }, (_, i) => i + 1).map((item) => (
              <tr key={item}>
                {column.map((col) => (
                  <td key={col.value}></td>
                ))}
                <td className="btn-action"></td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

const DriverList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data, isLoading, page } = useSelector((state) => state.drivers);
  const user = useSelector((state) => state.user.profile);

  const isDriverDeleteApproval = useFliptBoolean('FLAG_FE_DELETE_DRIVER_APPROVAL', false, user.id || 'anonymous');
  const isEnhancementReviewDriver = useFliptBoolean('FLAG_FE_ENHANCEMENT_REVIEW_DRIVER', false, user.id || 'anonymous');

  const { setShowDeleteDriverReasonModal, setShowConfirmation, showToast } = useAppContext();

  const COLUMN_DATA = isEnhancementReviewDriver ? newColumn : column;

  useEffect(() => {
    dispatch(getAllDrivers(page));
  }, [page]);

  const handleDetail = (e) => {
    const id = e.target.parentElement.parentElement.dataset['key'];
    dispatch(resetSelectedDriver());
    navigate(`/driver/${id}`);
  };

  const handleEdit = (e) => {
    const id = e.target.parentElement.parentElement.dataset['key'];
    dispatch(resetSelectedDriver());
    navigate(`/driver/${id}?is_edit=true`);
  };

  const handleDelete = (e) => {
    const id = e.target.parentElement.parentElement.dataset['key'];

    if (isDriverDeleteApproval) {
      setShowDeleteDriverReasonModal(true);
      dispatch(getDetailDriver(id));
    } else {
      setShowConfirmation({
        message: 'Apakah anda ingin menghapus data ini?',
        show: true,
        onClick: async () => {
          try {
            trackEvent('driver_deleted', {
              driver_id: id,
              source: 'driver_list',
            });
            await dispatch(deleteDriverById(id)).unwrap();
            showToast({ type: 'success', message: 'Berhasil menghapus data!' });
            dispatch(getAllDrivers(1));
            dispatch(setPage(1));
          } catch (err) {
            showToast({ type: 'error', message: 'Gagal menghapus data!' + `[${err?.message}]` });
          }
        },
      });
    }
  };

  const getDriverStatus = (isDeactivated, isWaitingApproval) => {
    if (isDriverDeleteApproval) {
      if (isWaitingApproval) return 'waiting_approval';
      if (isDeactivated) return 'inactive';
      return 'active';
    }

    if (isDeactivated) return 'inactive';
    return 'active';
  };

  const processedData = data?.users?.map((item) => ({
    id: item.id,
    name: item.name,
    email: item.email,
    phone_number: item.phone,
    status: getDriverStatus(item.is_deactivated, item.is_waiting_approval),
    rating: item.rating?.driver_rating ?? item.driver_rating ?? null,
  }));

  const driverListView = () => {
    if (processedData?.length === 0 && !isLoading) {
      return <EmptyState />;
    } else {
      return (
        <>
          {isDriverDeleteApproval || isEnhancementReviewDriver ? (
            <CustomDriverTable
              data={processedData}
              currentPage={page}
              handleDetail={handleDetail}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              column={COLUMN_DATA}
            />
          ) : (
            <Table
              column={column}
              actionBtn
              onDetail={handleDetail}
              onDel={handleDelete}
              onEdit={handleEdit}
              data={processedData}
              currentPage={page}
            />
          )}
          <PaginationTable
            currentPage={page}
            onPageChange={(newPage) => dispatch(setPage(newPage))}
            totalCount={data?.pagination?.total_data}
          />
        </>
      );
    }
  };

  return (
    <div>
      <TableWrapper
        icon={<DriverIcon fill="#009EF7" width="25px" height="25px" />}
        title="Driver"
        CustomFilterComponent={true}
      >
        {isLoading ? <LoadingSpinner /> : driverListView()}
      </TableWrapper>
    </div>
  );
};

export default DriverList;
